import { useMemo, useState } from "react";
import { MagnifyingGlass, Buildings } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TaxIdChecksumBadge } from "./TaxIdChecksumBadge";
import { SubVerticalGridItem } from "./SubVerticalGridItem";
import { formatVkn } from "./utils";
import type { BusinessStepDraft, OnboardingLabels, StepError, SubVerticalCategory, SubVerticalOption } from "./types";

export interface BusinessStepProps {
  value: BusinessStepDraft;
  onChange: (patch: Partial<BusinessStepDraft>) => void;
  options: SubVerticalOption[];
  errors?: StepError[];
  labels: OnboardingLabels;
  locale: "tr" | "en";
  className?: string;
}

function findError(errors: StepError[] | undefined, path: string) {
  return errors?.find((e) => e.path === path)?.message;
}

export function BusinessStep({ value, onChange, options, errors, labels, locale, className }: BusinessStepProps) {
  const [filter, setFilter] = useState<SubVerticalCategory | "all">("all");
  const [search, setSearch] = useState("");

  const nameError = findError(errors, "business.businessName");
  const taxIdError = findError(errors, "business.taxId");
  const subVerticalError = findError(errors, "business.subVerticalKey");

  const categories = useMemo(() => {
    const set = new Set<SubVerticalCategory>();
    options.forEach((o) => set.add(o.category));
    return Array.from(set);
  }, [options]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return options.filter((o) => {
      if (filter !== "all" && o.category !== filter) return false;
      if (!q) return true;
      return (
        o.displayName[locale].toLowerCase().includes(q) ||
        o.description[locale].toLowerCase().includes(q)
      );
    });
  }, [options, filter, search, locale]);

  return (
    <div className={cn("space-y-6", className)}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FieldShell
          id="onboarding-business-name"
          label={labels.business.name}
          required
          error={nameError}
          leadingIcon={<Buildings size={16} />}
        >
          <Input
            id="onboarding-business-name"
            placeholder={labels.business.namePlaceholder}
            value={value.businessName}
            onChange={(e) => onChange({ businessName: e.target.value })}
            aria-invalid={!!nameError}
            autoComplete="organization"
            className="h-14 pl-10 text-base"
          />
        </FieldShell>

        <FieldShell
          id="onboarding-tax-id"
          label={labels.business.taxId}
          required
          error={taxIdError}
          rightSlot={<TaxIdChecksumBadge value={value.taxId} countryCode={value.countryCode} labels={labels.taxIdBadge} />}
        >
          <Input
            id="onboarding-tax-id"
            inputMode="numeric"
            placeholder={labels.business.taxIdPlaceholder}
            value={value.taxId}
            onChange={(e) => onChange({ taxId: formatVkn(e.target.value) })}
            aria-invalid={!!taxIdError}
            maxLength={10}
            className="h-14 font-mono text-base tracking-widest"
          />
        </FieldShell>
      </div>

      <FieldShell id="onboarding-country" label={labels.business.countryCode}>
        <select
          id="onboarding-country"
          value={value.countryCode}
          onChange={(e) => onChange({ countryCode: e.target.value })}
          className={cn(
            "flex h-14 w-full rounded-md border border-biz-input bg-biz-background px-3 text-base text-biz-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
          )}
        >
          <option value="TR">🇹🇷 Türkiye (TR)</option>
          <option value="DE">🇩🇪 Deutschland (DE)</option>
          <option value="AE">🇦🇪 United Arab Emirates (AE)</option>
          <option value="GB">🇬🇧 United Kingdom (GB)</option>
        </select>
      </FieldShell>

      <div>
        <div className="mb-3 flex flex-col gap-1">
          <Label className="inline-flex items-center gap-1 text-sm font-medium text-biz-foreground">
            {labels.business.subVertical}
            <span className="text-biz-danger">*</span>
          </Label>
          <p className="text-xs text-biz-foreground-muted">{labels.business.subVerticalSubtitle}</p>
        </div>

        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted" />
            <Input
              type="search"
              placeholder={labels.business.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-11 pl-10"
              aria-label={labels.business.searchPlaceholder}
            />
          </div>
          <div
            role="tablist"
            aria-label="Vertical filters"
            className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
              {labels.business.filterAll}
            </FilterChip>
            {categories.map((cat) => (
              <FilterChip key={cat} active={filter === cat} onClick={() => setFilter(cat)}>
                {labels.business.categories[cat]}
              </FilterChip>
            ))}
          </div>
        </div>

        <div
          role="radiogroup"
          aria-label={labels.business.subVertical}
          aria-invalid={!!subVerticalError}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {filtered.map((opt) => (
            <SubVerticalGridItem
              key={opt.key}
              option={opt}
              selected={value.subVerticalKey === opt.key}
              locale={locale}
              labels={labels.business}
              onSelect={(key) => onChange({ subVerticalKey: key })}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="rounded-biz-card border border-dashed border-biz-border p-6 text-center text-sm text-biz-foreground-muted">
            —
          </div>
        )}

        <p className="mt-3 text-xs text-biz-foreground-muted">{labels.business.subVerticalHint}</p>
        {subVerticalError && (
          <p role="alert" className="mt-2 text-xs font-medium text-biz-danger">
            {subVerticalError}
          </p>
        )}
      </div>
    </div>
  );
}

interface FieldShellProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  leadingIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  children: React.ReactNode;
}

function FieldShell({ id, label, required, error, leadingIcon, rightSlot, children }: FieldShellProps) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Label htmlFor={id} className="inline-flex items-center gap-1 text-sm font-medium text-biz-foreground">
          {label}
          {required && <span className="text-biz-danger">*</span>}
        </Label>
        {rightSlot}
      </div>
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-biz-foreground-muted">
            {leadingIcon}
          </span>
        )}
        {children}
      </div>
      {error && (
        <p role="alert" className="mt-1.5 text-xs font-medium text-biz-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterChip({ active, onClick, children }: FilterChipProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-9 shrink-0 items-center justify-center rounded-full border px-3 text-xs font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2",
        active
          ? "border-biz-primary bg-biz-primary text-biz-primary-foreground"
          : "border-biz-border bg-biz-background text-biz-foreground-muted hover:border-biz-primary/50 hover:text-biz-foreground",
      )}
    >
      {children}
    </button>
  );
}
